import { IconProps } from '@euler/app/components/icons/types';
import {
  DateTimePicker,
  useDateTimePicker,
} from '@euler/components/DateTimePicker';
import { Input } from '@euler/components/form/Input';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { formatDate } from '@euler/utils/formatters';
import { usePersistFn } from '@euler/utils/hooks';
import { memo } from 'react';
import { Platform } from 'react-native';
import { Path, Svg } from 'react-native-svg';
import { useOrderFlowContext } from '../../../../OrderFlowContext';
import { Cell } from '../components/Cell';
import { IconWrapper } from '../components/IconWrapper';
import { Row } from '../components/Row';
import { useOrderFormStyles } from '../components/styles';

export const InsuranceExpiryDateRow = memo(
  ({ required, ...props }: { required?: boolean; disabled?: boolean }) => {
    const {
      theme,
      formController: { form },
    } = useOrderFlowContext();

    const { iconColor, placeholderColor } = theme;

    const [isDateTimePickerVisible, showDateTimePicker, hideDateTimePicker] =
      useDateTimePicker();

    const onConfirm = usePersistFn((value: Date) => {
      form.insuranceExpiryDate.update(value);
      hideDateTimePicker();
    });

    // Fix 'invalid date format' error
    const value = form.insuranceExpiryDate.value
      ? formatDate(form.insuranceExpiryDate.value)
      : '';
    const styles = useOrderFormStyles();

    return (
      <Row
        errorProp="insuranceExpiryDate"
        detailIcon={'disclosure'}
        icon={
          <IconWrapper>
            <RowIcon color={iconColor} />
          </IconWrapper>
        }
        onPress={showDateTimePicker}
        {...props}
      >
        <Cell label="保险到期日期" required={required}>
          <Input
            style={styles.formInput}
            placeholderTextColor={placeholderColor}
            value={value}
            placeholder={Platform.OS === 'web' ? 'YYYY/MM/DD' : '可选'}
            errorMsg={form.insuranceExpiryDate.error}
            editable={Platform.OS === 'web' && !props.disabled}
            pointerEvents={Platform.OS === 'web' ? undefined : 'none'}
            onChangeText={
              Platform.OS === 'web'
                ? form.insuranceExpiryDate.update
                : undefined
            }
            onBlur={
              Platform.OS === 'web'
                ? form.insuranceExpiryDate.onBlur
                : undefined
            }
          />
          <DateTimePicker
            isVisible={isDateTimePickerVisible}
            mode="date"
            date={
              form.insuranceExpiryDate.value
                ? new Date(form.insuranceExpiryDate.value)
                : undefined
            }
            onConfirm={onConfirm}
            onCancel={hideDateTimePicker}
            confirmTextIOS="确认"
            cancelTextIOS="关闭"
          />
        </Cell>
      </Row>
    );
  },
);

const RowIcon = memo(({ size, color = '#07F', style, ...props }: IconProps) => {
  const { rpx } = useResponsiveContext();
  return (
    <Svg
      width={size ?? rpx(24)}
      height={size ?? rpx(23)}
      fill="none"
      viewBox="0 0 1024 1024"
      style={style}
      {...props}
    >
      <Path
        d="M441.6 306.8L403 288.6c-3.3-1.6-7.3 0.2-8.4 3.7-17.5 58.5-45.2 110.1-82.2 153.6-1.3 1.6-1.8 3.7-1.2 5.6l13.2 43.5c1.3 4.4 7 5.7 10.2 2.4 7.7-8.1 15.4-16.9 23.1-26V656c0 4.4 3.6 8 8 8H403c4.4 0 8-3.6 8-8V393.1c13.8-25.1 25.1-51.7 33.6-79 1-2.9-0.3-6-3-7.3z m26.8 9.2v127.2c0 4.4 3.6 8 8 8h65.9v18.6h-94.9c-4.4 0-8 3.6-8 8v35.6c0 4.4 3.6 8 8 8h55.1c-19.1 30.8-42.4 55.7-71 76-2.6 1.8-3.3 5.4-1.6 8.1l22.8 36.5c1.9 3.1 6.2 3.8 8.9 1.4 31.6-26.8 58.7-62.9 80.6-107.6v120c0 4.4 3.6 8 8 8h36.2c4.4 0 8-3.6 8-8V536c21.3 41.7 47.5 77.5 78.1 106.9 2.6 2.5 6.8 2.1 8.9-0.7l26.3-35.3c2-2.7 1.4-6.5-1.2-8.4-30.5-22.6-54.2-47.8-72.3-76.9h59c4.4 0 8-3.6 8-8V478c0-4.4-3.6-8-8-8h-98.8v-18.6h66.7c4.4 0 8-3.6 8-8V316c0-4.4-3.6-8-8-8H476.4c-4.4 0-8 3.6-8 8z m51.5 42.8h97.9v41.6h-97.9v-41.6z"
        fill={color}
      />
      <Path
        d="M866.9 169.9L527.1 54.1C523 52.7 517.5 52 512 52s-11 0.7-15.1 2.1L157.1 169.9c-8.3 2.8-15.1 12.4-15.1 21.2v482.4c0 8.8 5.7 20.4 12.6 25.9L499.3 968c3.5 2.7 8 4.1 12.6 4.1s9.2-1.4 12.6-4.1l344.7-268.6c6.9-5.4 12.6-17 12.6-25.9V191.1c0.2-8.8-6.6-18.3-14.9-21.2zM810 654.3L512 886.5 214 654.3V226.7l298-101.6 298 101.6v427.6z"
        fill={color}
      />
    </Svg>
  );
});
